import { separator } from '@/shell/Menus/types';
import type { MenuItem } from '@/shell/Menus/types';

/**
 * The menu every text box in Windows shared.
 *
 * Undo, cut, copy, paste, delete, select all: one menu, drawn by the edit
 * control itself rather than by the program around it, which is why it looked
 * identical in Notepad, in the Run box and in Explorer's address bar. It works
 * the same way here — nothing has to opt in beyond being an `<input>` or a
 * `<textarea>`, and `ContextMenu` finds them on the way past.
 */

export type TextField = HTMLInputElement | HTMLTextAreaElement;

/** Input types that hold text a caret can sit in. Others have no menu. */
const TEXTUAL = new Set(['text', 'search', 'url', 'tel', 'email', 'password', 'number', '']);

/** The text field this event landed in, or null if it did not land in one. */
export function asTextField(target: EventTarget | null): TextField | null {
  if (target instanceof HTMLTextAreaElement) return target;
  if (target instanceof HTMLInputElement && TEXTUAL.has(target.type)) return target;
  return null;
}

/**
 * Selections are lost the moment the popup takes focus, so every command has
 * to put the caret back where the right-click found it before it runs. The
 * range is read when the menu is built — the last instant it is still true.
 */
function restore(el: TextField, start: number, end: number): void {
  el.focus({ preventScroll: true });
  try {
    el.setSelectionRange(start, end);
  } catch {
    // Some input types refuse a range even when they report one. The focus
    // still counted, and that is what the clipboard commands actually need.
  }
}

/**
 * Clipboard writes go through `execCommand` first: it is deprecated, and it is
 * also the only write that happens synchronously inside the gesture that asked
 * for it, with no permission prompt in front of it. The async API is the
 * fallback for browsers that have finally dropped it.
 */
function writeClipboard(text: string, cut: boolean): void {
  let done = false;
  try {
    done = document.execCommand(cut ? 'cut' : 'copy');
  } catch {
    done = false;
  }
  if (done || !navigator.clipboard) return;

  void navigator.clipboard.writeText(text).catch(() => undefined);
  // The async write copied the text but removed nothing, so a cut still owes
  // the field its deletion.
  if (cut) document.execCommand('delete');
}

/**
 * Paste is the one command that cannot be finished inside the gesture: reading
 * the clipboard is asynchronous everywhere, and in some browsers it asks the
 * visitor's permission first. `readText` is called immediately so the gesture
 * still counts; the caret is restored again on the way back, because anything
 * could have happened to it while the prompt was up.
 */
function paste(el: TextField, start: number, end: number): void {
  if (!navigator.clipboard?.readText) return;

  void navigator.clipboard
    .readText()
    .then((text) => {
      if (!text || !el.isConnected) return;
      restore(el, start, end);
      // `insertText` fires the input event React is listening for; writing to
      // `el.value` directly would leave a controlled field out of step with
      // the state behind it.
      document.execCommand('insertText', false, text);
    })
    .catch(() => undefined);
}

/**
 * The edit control's own context menu, for the field the pointer is over.
 *
 * A read-only field — Notepad's document, on a disk that takes no writes —
 * greys everything that would change the text and keeps the two commands that
 * only read it. That is exactly what the real control did, and it is why the
 * menu is worth having on a page nobody can type into.
 */
export function textFieldMenu(el: TextField): MenuItem[] {
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const selected = el.value.slice(start, end);

  const writable = !el.readOnly && !el.disabled;
  const hasSelection = selected.length > 0;

  const command = (run: () => void) => () => {
    restore(el, start, end);
    run();
  };

  return [
    {
      label: '&Undo',
      disabled: !writable,
      onSelect: command(() => document.execCommand('undo')),
    },
    separator,
    {
      label: 'Cu&t',
      disabled: !writable || !hasSelection,
      onSelect: command(() => writeClipboard(selected, true)),
    },
    {
      label: '&Copy',
      disabled: !hasSelection,
      onSelect: command(() => writeClipboard(selected, false)),
    },
    {
      label: '&Paste',
      disabled: !writable,
      onSelect: () => paste(el, start, end),
    },
    {
      label: '&Delete',
      disabled: !writable || !hasSelection,
      onSelect: command(() => document.execCommand('delete')),
    },
    separator,
    {
      label: 'Select &All',
      disabled: el.value.length === 0,
      onSelect: command(() => el.select()),
    },
  ];
}
